import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import React, { FC } from 'react'

import { RepairOrderExpanded } from '../../../sdk'
import { formatCurrency } from '../../../utils'
import { getRepairOrderPricing } from '../../../utils/getRepairOrderPricing'
import { AddDiscountButton } from '../../Estimates/TaxExemptCheckbox/AddDiscountButton'
import { EditDiscountButton } from '../../Estimates/TaxExemptCheckbox/EditDiscountButton'
import { RepairOrderCustomFeeExemptCheckbox } from '../../Estimates/TaxExemptCheckbox/RepairOrderCustomFeeExemptCheckbox'
import { RepairOrderShopHazmatFeeExemptCheckbox } from '../../Estimates/TaxExemptCheckbox/RepairOrderShopHazmatFeeExemptCehckbox'
import { RepairOrderShopSuppliesFeeExemptCheckbox } from '../../Estimates/TaxExemptCheckbox/RepairOrderShopSuppliesFeeExemptCheckbox'
import { RepairOrderTaxExemptCheckbox } from '../../Estimates/TaxExemptCheckbox/RepairOrderTaxExemptCheckbox'
import { AuthorizationWarning } from './AuthorizationWarning'
import SummaryPanelTableRow from './SummaryPanelTableRow'

interface Props {
  repairOrder: RepairOrderExpanded
  readOnly: boolean
  authorizedAmount?: number
  showUpdating?: boolean
  enableDiscount?: boolean
  profitSummary?: React.ReactNode
}

const SummaryPanel: FC<Props> = ({
  repairOrder,
  readOnly,
  authorizedAmount,
  showUpdating,
  enableDiscount,
  profitSummary
}) => {
  const repairOrderPricing = repairOrder.entry!.info!.isEstimate
    ? repairOrder.totalPricing!
    : repairOrder.authorizedPricing!
  const {
    totalLaborPrice,
    totalPartsPrice,
    totalSublet,
    totalTaxes,
    shopSuppliesFee,
    shopHazmatFee,
    customFees,
    warrantyTotal,
    discountTotal,
    repairOrderDiscount,
    serviceDiscount,
    estimateTotal,
    estimateTotalWithSurcharge
  } = getRepairOrderPricing(repairOrderPricing)

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="SummaryPanel">
      <Stack spacing={1}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems={'flex-end'}>
          <Typography variant="h4">Summary</Typography>
          {showUpdating && <CircularProgress size="1.5rem" />}
          {!readOnly && <AuthorizationWarning repairOrder={repairOrder} />}
          {!readOnly && enableDiscount && (
            <AddDiscountButton repairOrder={repairOrder} />
          )}
        </Box>
        <Divider />
        <SummaryPanelTableRow
          title="Total Labor Price"
          value={formatCurrency(totalLaborPrice, true)}
        />
        <SummaryPanelTableRow
          title="Total Parts Price"
          value={formatCurrency(totalPartsPrice, true)}
        />
        {!(readOnly && Math.abs(totalSublet) < 0.01) && (
          <SummaryPanelTableRow
            title="Total Sublet Price"
            value={formatCurrency(totalSublet, true)}
          />
        )}
        {!(readOnly && Math.abs(shopSuppliesFee) < 0.01) && (
          <SummaryPanelTableRow
            title="Supplies Fee"
            value={formatCurrency(shopSuppliesFee, true)}
            additionalItem={
              !readOnly ? (
                <RepairOrderShopSuppliesFeeExemptCheckbox
                  size="small"
                  repairOrder={repairOrder.entry!}
                />
              ) : undefined
            }
          />
        )}
        {!(readOnly && Math.abs(shopHazmatFee) < 0.01) && (
          <SummaryPanelTableRow
            title="Hazmat Fee"
            value={formatCurrency(shopHazmatFee, true)}
            additionalItem={
              !readOnly ? (
                <RepairOrderShopHazmatFeeExemptCheckbox
                  size="small"
                  repairOrder={repairOrder.entry!}
                />
              ) : undefined
            }
          />
        )}
        {!(readOnly && Math.abs(customFees) < 0.01) && (
          <SummaryPanelTableRow
            title="Fees"
            value={formatCurrency(customFees, true)}
            additionalItem={
              !readOnly ? (
                <RepairOrderCustomFeeExemptCheckbox
                  size="small"
                  repairOrder={repairOrder.entry!}
                />
              ) : undefined
            }
          />
        )}
        {!(readOnly && Math.abs(warrantyTotal) < 0.01) && (
          <SummaryPanelTableRow
            title="Warranty"
            value={formatCurrency(warrantyTotal * -1, true)}
          />
        )}
        {readOnly ? (
          !(Math.abs(discountTotal) < 0.01) && (
            <SummaryPanelTableRow
              title="Discounts"
              value={formatCurrency(discountTotal * -1, true)}
            />
          )
        ) : (
          <>
            <SummaryPanelTableRow
              title="Services Discounts"
              value={formatCurrency(serviceDiscount * -1, true)}
            />
            {enableDiscount && (
              <SummaryPanelTableRow
                title="Repair Order Discounts"
                value={formatCurrency(repairOrderDiscount * -1, true)}
                additionalItem={
                  !readOnly ? (
                    <EditDiscountButton repairOrder={repairOrder} />
                  ) : undefined
                }
              />
            )}
          </>
        )}

        {authorizedAmount !== undefined && (
          <SummaryPanelTableRow
            title="Authorized Amount"
            value={formatCurrency(authorizedAmount, true)}
          />
        )}
        <SummaryPanelTableRow
          title="Taxes"
          value={formatCurrency(totalTaxes, true)}
          additionalItem={
            !readOnly ? (
              <RepairOrderTaxExemptCheckbox
                size="small"
                repairOrder={repairOrder.entry!}
              />
            ) : undefined
          }
        />
        <SummaryPanelTableRow
          title={`${
            repairOrder?.entry!.info!.isEstimate ? 'Estimate' : 'Repair Order'
          } Total`}
          value={formatCurrency(estimateTotal, true)}
          variant="h6"
        />
        {estimateTotalWithSurcharge !== estimateTotal && (
          <SummaryPanelTableRow
            title={`Total With Credit Card Surcharge`}
            value={formatCurrency(estimateTotalWithSurcharge, true)}
            variant="h6"
          />
        )}
        {profitSummary}
      </Stack>
    </Paper>
  )
}

export default SummaryPanel
